// Email reminder subscriptions — the modal surface (docs/reminders.md).
// Rendered inside a Dialog off the header's mail icon (Upload's pattern).
// Lists the user's subscriptions (add / edit / pause / remove) and an add/edit
// form whose steps are filtered to what the user can act on. Editing is
// copy-on-write, done in one server call (lib/reminders).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Body1,
  Button,
  Badge,
  Checkbox,
  Divider,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Option,
  Spinner,
  Text,
  ToggleButton,
  Toast,
  ToastTitle,
  useToastController,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from '@fluentui/react-components'
import { AddRegular, EditRegular, DeleteRegular, PauseRegular, PlayRegular } from '@fluentui/react-icons'
import { errorMessage } from '../api/errors'
import type { Session } from '../lib/identity'
import type { ReminderSubscription } from '../lib/types'
import { TOASTER_ID } from '../lib/constants'
import { loadMyReminders } from '../lib/data'
import {
  DAY_TOKENS,
  HOUR_OPTIONS,
  hourLabel,
  stepOptions,
  deriveDisplayStatus,
  formatSchedule,
  emptyForm,
  rowToForm,
  validateForm,
  saveNewSubscription,
  saveEditedSubscription,
  setSubscriptionPaused,
  cancelSubscription,
  type ReminderForm,
} from '../lib/reminders'
import { popupPositionProps, usePopupListboxClass } from '../components/popupPositioning'

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0 },
  intro: { color: tokens.colorNeutralForeground3 },
  list: { display: 'flex', flexDirection: 'column', gap: '10px' },
  card: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  cardHead: { display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'space-between' },
  cardTitle: { display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 },
  meta: { color: tokens.colorNeutralForeground3, fontSize: '12px' },
  rowActions: { display: 'flex', gap: '4px', flexShrink: 0 },
  daysRow: { display: 'flex', flexWrap: 'wrap', gap: '4px' },
  // ToggleButton's own "checked" look is a near-invisible neutral tint
  // (`colorNeutralBackground1Selected`, same family as the unchecked
  // background) — selected vs. not is unmistakable only once this wins.
  // Passed last into `mergeClasses` (never a template join, per
  // app/README.md's Fluent UI v9 gotchas), so Griffel's own property-level
  // merge lets it override the built-in checked/hover rules deterministically
  // rather than racing them on CSS specificity. `shorthands.borderColor`
  // (not a plain `borderColor` key) so the override lands on the same
  // per-side properties Fluent's own checked style sets.
  dayChecked: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    ...shorthands.borderColor(tokens.colorBrandBackground),
    ':hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
      color: tokens.colorNeutralForegroundOnBrand,
      ...shorthands.borderColor(tokens.colorBrandBackgroundHover),
    },
    ':active': {
      backgroundColor: tokens.colorBrandBackgroundPressed,
      color: tokens.colorNeutralForegroundOnBrand,
      ...shorthands.borderColor(tokens.colorBrandBackgroundPressed),
    },
  },
  // Field sizing/alignment: equal-width date pair that won't stretch one
  // Field taller than the other just because its sibling has a hint line.
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', alignItems: 'start' },
  // A native date input's own intrinsic width can exceed a narrow grid track
  // (same fix as FilterButton's `rangeInput`).
  dateInput: { minWidth: 0, width: '100%' },
  // Dropdown has a ~250px default min-width (app/README.md gotcha) — without
  // this it won't shrink to the field's width and can push the dialog wider.
  hourDropdown: { minWidth: 0, width: '100%' },
  nameInput: { width: '100%', minWidth: 0 },
  checks: { display: 'flex', flexDirection: 'column', gap: '2px' },
  formActions: { display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '4px' },
  empty: { color: tokens.colorNeutralForeground3, padding: '8px 0' },
})

function statusColor(s: string): 'success' | 'warning' | 'informative' | 'subtle' {
  return s === 'Scheduled' ? 'success' : s === 'Paused' ? 'warning' : s === 'Not started' ? 'informative' : 'subtle'
}

export function RemindersScreen({ session }: { session: Session }) {
  const s = useStyles()
  const { dispatchToast } = useToastController(TOASTER_ID)

  const [subs, setSubs] = useState<ReminderSubscription[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [mode, setMode] = useState<'list' | 'form'>('list')
  const [editing, setEditing] = useState<ReminderSubscription | null>(null)
  const [form, setForm] = useState<ReminderForm>(() => emptyForm())
  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)

  const stepOpts = useMemo(() => stepOptions(session), [session])

  // This screen renders inside App.tsx's own reminders Dialog (never as a
  // standalone page), so the hour Dropdown below needs the same fix every
  // other in-dialog Fluent popup on this project needs (app/README.md's
  // Fluent UI v9 gotcha: an unmounted popup "closes as fast as it opens"
  // without an explicit `mountNode`/`inlinePopup`) — resolved the same way
  // `AssociatedEntitiesEditor` resolves its own dialog host, via `.closest`
  // rather than a prop threaded down from App.tsx, since this component has
  // no other reason to know about its parent dialog.
  const rootRef = useRef<HTMLDivElement>(null)
  const [dialogHost, setDialogHost] = useState<HTMLElement | null>(null)
  const popupListboxClass = usePopupListboxClass()
  useEffect(() => {
    setDialogHost(rootRef.current?.closest('[role="dialog"]') as HTMLElement | null)
  }, [])

  // The API scopes the list to the caller, so there is nothing to pass.
  const reload = useCallback(async () => {
    setError(null)
    try {
      setSubs(await loadMyReminders())
    } catch (e) {
      setError(errorMessage(e))
      setSubs([])
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const toast = (msg: string) =>
    dispatchToast(<Toast><ToastTitle>{msg}</ToastTitle></Toast>, { intent: 'success' })

  const openNew = () => {
    setEditing(null)
    setForm(emptyForm())
    setFormError(null)
    setMode('form')
  }
  const openEdit = (row: ReminderSubscription) => {
    setEditing(row)
    setForm(rowToForm(row))
    setFormError(null)
    setMode('form')
  }

  const save = async () => {
    const err = validateForm(form)
    if (err) {
      setFormError(err)
      return
    }
    setSaving(true)
    setFormError(null)
    try {
      if (editing) await saveEditedSubscription(editing, form)
      else await saveNewSubscription(form)
      toast(editing ? 'Reminder updated' : 'Reminder created')
      setMode('list')
      setEditing(null)
      await reload()
    } catch (e) {
      setFormError(errorMessage(e))
    } finally {
      setSaving(false)
    }
  }

  const rowAction = async (row: ReminderSubscription, fn: () => Promise<void>, msg: string) => {
    setBusyId(row.id)
    try {
      await fn()
      toast(msg)
      await reload()
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setBusyId(null)
    }
  }

  const toggle = <T,>(list: T[], v: T): T[] => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v])

  // ---- form view ----
  if (mode === 'form') {
    return (
      <div className={s.root} ref={rootRef}>
        <Field label="Name (optional)">
          <Input
            className={s.nameInput}
            value={form.label}
            placeholder="e.g. Morning approvals"
            onChange={(_, d) => setForm({ ...form, label: d.value })}
          />
        </Field>

        <Field label="Days of the week">
          <div className={s.daysRow}>
            {DAY_TOKENS.map((day) => (
              <ToggleButton
                key={day}
                size="small"
                checked={form.days.includes(day)}
                className={mergeClasses(form.days.includes(day) && s.dayChecked)}
                onClick={() => setForm({ ...form, days: toggle(form.days, day) })}
              >
                {day}
              </ToggleButton>
            ))}
          </div>
        </Field>

        <div className={s.grid2}>
          <Field label="Start date">
            <Input
              className={s.dateInput}
              type="date"
              value={form.startDate}
              onChange={(_, d) => setForm({ ...form, startDate: d.value })}
            />
          </Field>
          <Field label="End date (optional)" hint="Leave blank for no end date.">
            <Input
              className={s.dateInput}
              type="date"
              value={form.endDate}
              onChange={(_, d) => setForm({ ...form, endDate: d.value })}
            />
          </Field>
        </div>

        {/* Whole hours only — the digest goes out on an hourly tick. */}
        <Field label="Time of day (Eastern)">
          <Dropdown
            className={s.hourDropdown}
            {...popupPositionProps(dialogHost)}
            listbox={{ className: popupListboxClass }}
            value={hourLabel(form.time)}
            selectedOptions={[form.time]}
            onOptionSelect={(_, d) => d.optionValue && setForm({ ...form, time: d.optionValue })}
          >
            {HOUR_OPTIONS.map((o) => (
              <Option key={o.value} value={o.value}>
                {o.label}
              </Option>
            ))}
          </Dropdown>
        </Field>

        <Field label="Steps to be reminded about" hint="Only steps you can act on are shown.">
          {stepOpts.length === 0 ? (
            <Text className={s.empty}>You have no steps you can act on.</Text>
          ) : (
            <div className={s.checks}>
              {stepOpts.map((o) => (
                <Checkbox
                  key={o.key}
                  label={o.label}
                  checked={form.steps.includes(o.key)}
                  onChange={() => setForm({ ...form, steps: toggle(form.steps, o.key) })}
                />
              ))}
            </div>
          )}
        </Field>

        {formError && (
          <MessageBar intent="error">
            <MessageBarBody>{formError}</MessageBarBody>
          </MessageBar>
        )}

        <div className={s.formActions}>
          <Button appearance="secondary" disabled={saving} onClick={() => setMode('list')}>
            Cancel
          </Button>
          <Button appearance="primary" disabled={saving} icon={saving ? <Spinner size="tiny" /> : undefined} onClick={save}>
            {editing ? 'Save changes' : 'Create reminder'}
          </Button>
        </div>
      </div>
    )
  }

  // ---- list view ----
  return (
    <div className={s.root}>
      <Body1 className={s.intro}>
        Get an email digest of what’s waiting on you — counts and totals only, on the schedule you choose. Eastern time.
      </Body1>

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {subs === null ? (
        <Spinner size="small" label="Loading your reminders…" />
      ) : subs.length === 0 ? (
        <Text className={s.empty}>You don’t have any reminders yet.</Text>
      ) : (
        <div className={s.list}>
          {subs.map((row) => {
            const status = deriveDisplayStatus(row)
            const paused = row.status === 'paused'
            const busy = busyId === row.id
            const steps = (row.steps ?? []).filter(Boolean).length
            return (
              <div key={row.id} className={s.card}>
                <div className={s.cardHead}>
                  <div className={s.cardTitle}>
                    <Text weight="semibold" truncate>
                      {row.label || 'Reminder'}
                    </Text>
                    <Badge appearance="tint" color={statusColor(status)} size="small">
                      {status}
                    </Badge>
                  </div>
                  <div className={s.rowActions}>
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={paused ? <PlayRegular /> : <PauseRegular />}
                      disabled={busy}
                      aria-label={paused ? 'Resume' : 'Pause'}
                      title={paused ? 'Resume' : 'Pause'}
                      onClick={() => rowAction(row, () => setSubscriptionPaused(row, !paused), paused ? 'Reminder resumed' : 'Reminder paused')}
                    />
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<EditRegular />}
                      disabled={busy}
                      aria-label="Edit"
                      title="Edit"
                      onClick={() => openEdit(row)}
                    />
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<DeleteRegular />}
                      disabled={busy}
                      aria-label="Remove"
                      title="Remove"
                      onClick={() => rowAction(row, () => cancelSubscription(row), 'Reminder removed')}
                    />
                  </div>
                </div>
                <Text className={s.meta}>{formatSchedule(row)}</Text>
                <Text className={s.meta}>
                  {steps} step{steps === 1 ? '' : 's'}
                </Text>
              </div>
            )
          })}
        </div>
      )}

      <Divider />
      <div>
        <Button appearance="primary" icon={<AddRegular />} onClick={openNew}>
          Add reminder
        </Button>
      </div>
    </div>
  )
}
